// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.
import type { CSSProperties } from "react";

// every component with a `tone` prop paints through the same five css vars, so a tone, a custom `color`
// and a className override all go through one path:
//   --tone                     solid fill                 bg-[color:var(--tone)]
//   --tone-foreground          text / icons on the fill   text-[color:var(--tone-foreground)]
//   --tone-soft                tinted surface             bg-[color:var(--tone-soft)]
//   --tone-soft-foreground     tone text on the tint and on any neutral surface
//   --tone-border              outline and divider color  border-[color:var(--tone-border)]
export type Tone = "neutral" | "brand" | "success" | "warning" | "danger" | "info";

export const TONES: Tone[] = ["neutral", "brand", "success", "warning", "danger", "info"];

// static strings so tailwind sees every class; spread with cn(toneClasses[tone], ...)
export const toneClasses: Record<Tone, string> = {
  neutral:
    "[--tone:rgb(var(--wui-foreground))] [--tone-foreground:rgb(var(--wui-background))] [--tone-soft:color-mix(in_oklab,rgb(var(--wui-foreground))_7%,transparent)] [--tone-soft-foreground:rgb(var(--wui-foreground))] [--tone-border:color-mix(in_oklab,rgb(var(--wui-foreground))_16%,transparent)]",
  brand:
    "[--tone:rgb(var(--wui-brand))] [--tone-foreground:rgb(var(--wui-brand-foreground))] [--tone-soft:rgb(var(--wui-brand-soft))] [--tone-soft-foreground:rgb(var(--wui-brand-soft-foreground))] [--tone-border:color-mix(in_oklab,rgb(var(--wui-brand))_45%,transparent)]",
  success:
    "[--tone:rgb(var(--wui-success))] [--tone-foreground:rgb(var(--wui-success-foreground))] [--tone-soft:rgb(var(--wui-success-soft))] [--tone-soft-foreground:rgb(var(--wui-success-soft-foreground))] [--tone-border:color-mix(in_oklab,rgb(var(--wui-success))_45%,transparent)]",
  warning:
    "[--tone:rgb(var(--wui-warning))] [--tone-foreground:rgb(var(--wui-warning-foreground))] [--tone-soft:rgb(var(--wui-warning-soft))] [--tone-soft-foreground:rgb(var(--wui-warning-soft-foreground))] [--tone-border:color-mix(in_oklab,rgb(var(--wui-warning))_55%,transparent)]",
  danger:
    "[--tone:rgb(var(--wui-destructive))] [--tone-foreground:rgb(var(--wui-destructive-foreground))] [--tone-soft:rgb(var(--wui-destructive-soft))] [--tone-soft-foreground:rgb(var(--wui-destructive-soft-foreground))] [--tone-border:color-mix(in_oklab,rgb(var(--wui-destructive))_45%,transparent)]",
  info: "[--tone:rgb(var(--wui-info))] [--tone-foreground:rgb(var(--wui-info-foreground))] [--tone-soft:rgb(var(--wui-info-soft))] [--tone-soft-foreground:rgb(var(--wui-info-soft-foreground))] [--tone-border:color-mix(in_oklab,rgb(var(--wui-info))_45%,transparent)]",
};

// the same five vars from any css color (hex, rgb, oklch, var(--x)), for a `color` prop. text on the fill turns
// black or white by the color's lightness; tone text is mixed toward the foreground so it stays readable in both themes.
// force the text color with className="[--tone-foreground:white]"
export function colorVars(color: string | undefined): CSSProperties | undefined {
  if (!color) return undefined;
  return {
    "--tone": color,
    "--tone-foreground": `oklch(from ${color} clamp(0, (0.7 - l) * 1000, 1) 0 0)`,
    "--tone-soft": `color-mix(in oklab, ${color} 14%, transparent)`,
    "--tone-soft-foreground": `color-mix(in oklab, ${color} 72%, rgb(var(--wui-foreground)))`,
    "--tone-border": `color-mix(in oklab, ${color} 45%, transparent)`,
  } as CSSProperties;
}

// tone from a string prop (playground values, cms data), falling back to neutral
export function toTone(value: unknown, fallback: Tone = "neutral"): Tone {
  return TONES.includes(value as Tone) ? (value as Tone) : fallback;
}
